import type { ReactNode } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';
import { FadeIn } from '../animations';
import { Box } from './Box';
import { VStack } from './Stack';
import { Text } from './Text';

interface BadgeConfig {
  text: string;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
}

interface PageHeaderProps {
  badge?: string | BadgeConfig;
  title: string;
  subtitle?: string;
  rightContent?: ReactNode;
  paddingBottom?: 'lg' | 'xl';
  marginBottom?: 'xl';
}

export function PageHeader({
  badge,
  title,
  subtitle,
  rightContent,
  paddingBottom = 'lg',
  marginBottom,
}: Readonly<PageHeaderProps>) {
  const { width } = useWindowDimensions();
  const isDesktop = width >= tokens.breakpoints.desktop;
  const isTablet = width >= tokens.breakpoints.tablet;

  const badgeConfig: BadgeConfig | null = (() => {
    if (!badge) return null;
    if (typeof badge === 'string') {
      return {
        text: badge,
        backgroundColor: colors.primary.muted,
        borderColor: colors.primary.subtle,
        textColor: colors.primary.neon,
      };
    }
    return badge;
  })();

  return (
    <Box paddingBottom={paddingBottom} {...(marginBottom ? { marginBottom } : {})}>
      <Box
        flexDirection={isTablet ? 'row' : 'column'}
        justifyContent="space-between"
        alignItems={isTablet ? 'center' : 'stretch'}
        gap={isTablet ? undefined : '2xl'}
      >
        <VStack spacing="md" style={isTablet ? { flex: 1 } : undefined}>
          {badgeConfig && (
            <FadeIn delay={0} translateY={10}>
              <Box
                backgroundColor={badgeConfig.backgroundColor}
                paddingX="md"
                paddingY="xs"
                borderRadius="full"
                borderWidth={1}
                borderColor={badgeConfig.borderColor}
                alignSelf="flex-start"
              >
                <Text
                  variant="caption"
                  color={badgeConfig.textColor}
                  style={{
                    fontWeight: '600',
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    fontSize: Platform.select({ web: 11, default: 10 }),
                  }}
                >
                  {badgeConfig.text}
                </Text>
              </Box>
            </FadeIn>
          )}

          <FadeIn delay={100} translateY={15}>
            <Text
              variant={isDesktop ? 'h2' : 'h3'}
              color={colors.text.primary}
              style={{
                fontWeight: '700',
                letterSpacing: -0.5,
              }}
            >
              {title}
            </Text>
          </FadeIn>

          {subtitle && (
            <FadeIn delay={200} translateY={15}>
              <Text
                variant="body"
                color={colors.text.secondary}
                style={{
                  maxWidth: 500,
                  lineHeight: Platform.select({ web: 24, default: 22 }),
                }}
              >
                {subtitle}
              </Text>
            </FadeIn>
          )}
        </VStack>

        {rightContent && (
          <FadeIn delay={300} translateY={10}>
            <Box style={!isTablet ? { marginTop: tokens.spacing.md } : undefined}>
              {rightContent}
            </Box>
          </FadeIn>
        )}
      </Box>
    </Box>
  );
}
